import { useMutation } from '@tanstack/react-query'
import React from 'react'

import PlainActionButton from '@/components/misc/PlainActionButton'
import { ReferralLinkTableRow } from '@/components/misc/ReferralLinksTable/types'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useInvalidateQueries } from '@/hooks/useInvalidateQueries'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { QUERY_KEYS } from '@/lib/queryKeys'
import referralLinkService from '@/lib/services/referralLinkService'

interface Props {
  referralLink: ReferralLinkTableRow
  isCompassLinkValue: boolean
}

const ToggleIsCompassLinkButton: React.FC<Props> = ({
  referralLink,
  isCompassLinkValue,
}) => {
  const showGenericServerError = useShowGenericServerError()
  const notyf = useNotyf()
  const invalidateQueries = useInvalidateQueries()

  const markAsCompassLinkMutation = useMutation({
    mutationFn: () => referralLinkService.toggleIsCompassLink(referralLink.id),
    onSuccess: async () => {
      await invalidateQueries([
        QUERY_KEYS.shareableReferralLinks(),
        QUERY_KEYS.shareableRoundRobinReferralLinks(),
        QUERY_KEYS.referralLinks(),
      ])
      notyf.success(
        `Link successfully ${isCompassLinkValue ? 'marked' : 'unmarked'} as the Compass link`,
      )
    },
    onError: showGenericServerError,
  })

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <PlainActionButton
          className="hover-underline"
          onClick={() => markAsCompassLinkMutation.mutate()}
          isProcessing={markAsCompassLinkMutation.isPending}
          showIsProcessingText={false}
        >
          {isCompassLinkValue ? 'Mark' : 'Unmark'} as Compass link
        </PlainActionButton>
      </TooltipTrigger>

      <TooltipContent>
        {isCompassLinkValue ? (
          <>Associate referrals sent via Compass with this link</>
        ) : (
          <>Don't associate referrals sent via Compass with this link</>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

export default ToggleIsCompassLinkButton
